/**
 * MermaidDiagram — renders one Mermaid diagram (FRIDAY-67).
 *
 * The same component serves every surface: a task description, a comment
 * and a notebook page. The source is the text inside a ```mermaid fence;
 * Friday never stores anything else.
 *
 * The component owns three states — loading, ready and error — and never
 * breaks the content around it: a broken diagram shows a message and the
 * source, and the rest of the task or page stays usable.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useThemeId } from '@/features/theme/ThemeProvider'
import { findTheme } from '@/features/theme/themes'
import { cn } from '@/lib/utils'
import { renderMermaid, svgElementFrom } from './renderer'
import { mermaidThemeFor } from './theme'

/** How long we wait before re-rendering a diagram whose source changed. */
const RENDER_DEBOUNCE_MS = 350

export interface MermaidDiagramProps {
  /** The diagram source — the text inside the ```mermaid fence. */
  source: string
  className?: string
  /** Offer the "Show source" control. The page editor brings its own. */
  showSource?: boolean
  /** Called when the reader clicks the rendered diagram. */
  onActivate?: () => void
  /** Called when the diagram could not be rendered. */
  onError?: (message: string) => void
}

type Status = 'loading' | 'ready' | 'error'

export function MermaidDiagram({
  source,
  className,
  showSource = true,
  onActivate,
  onError,
}: MermaidDiagramProps) {
  const themeId = useThemeId()
  const theme = useMemo(() => findTheme(themeId), [themeId])
  const mermaidTheme = useMemo(() => mermaidThemeFor(theme), [theme])

  const debounced = useDebouncedSource(source, RENDER_DEBOUNCE_MS)
  const [status, setStatus] = useState<Status>('loading')
  const [message, setMessage] = useState('')
  const [sourceOpen, setSourceOpen] = useState(false)
  const [attempt, setAttempt] = useState(0)

  const hostRef = useRef<HTMLDivElement | null>(null)
  // Every render request gets a number; a result whose number is not the
  // newest is dropped, so a slow render can never replace a newer one.
  const requestRef = useRef(0)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    const request = ++requestRef.current
    let cancelled = false
    setStatus('loading')
    renderMermaid(debounced, mermaidTheme)
      .then((markup) => {
        if (cancelled || request !== requestRef.current) return
        const host = hostRef.current
        if (!host) return
        host.replaceChildren(svgElementFrom(markup))
        setStatus('ready')
      })
      .catch((error: unknown) => {
        if (cancelled || request !== requestRef.current) return
        const text = error instanceof Error ? error.message : 'The diagram could not be rendered.'
        setMessage(text)
        setStatus('error')
        onErrorRef.current?.(text)
      })
    return () => {
      cancelled = true
    }
  }, [debounced, mermaidTheme, attempt])

  // The source changed but the new diagram is not ready yet — the old one
  // must not pretend to be the current diagram.
  const pending = debounced !== source
  const ready = status === 'ready' && !pending

  return (
    <div className={cn('friday-mermaid', className)} data-mermaid-diagram="">
      <div
        ref={hostRef}
        className={cn('friday-mermaid-canvas', !ready && 'friday-mermaid-canvas--pending')}
        role={ready ? 'img' : undefined}
        aria-label="Mermaid diagram"
        onClick={onActivate}
      />
      {(pending || status === 'loading') && <p className="friday-mermaid-note">Rendering diagram…</p>}
      {status === 'error' && !pending && (
        <div className="friday-mermaid-error" role="alert">
          <p className="friday-mermaid-error-message">{message}</p>
          <button type="button" className="friday-mermaid-retry" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
          {showSource && <pre className="friday-mermaid-source">{source}</pre>}
        </div>
      )}
      {ready && showSource && (
        <button
          type="button"
          className="friday-mermaid-toggle"
          aria-expanded={sourceOpen}
          onClick={() => setSourceOpen((open) => !open)}
        >
          {sourceOpen ? 'Hide source' : 'Show source'}
        </button>
      )}
      {sourceOpen && <pre className="friday-mermaid-source">{source}</pre>}
    </div>
  )
}

/**
 * Returns the source to render: the current one, or — while the author is
 * typing — the last one that was rendered. The first source renders
 * immediately; only later changes are debounced.
 */
function useDebouncedSource(source: string, delay: number): string {
  const [value, setValue] = useState(source)
  const latest = useRef(source)

  useEffect(() => {
    if (source === latest.current) return
    const timer = setTimeout(() => {
      latest.current = source
      setValue(source)
    }, delay)
    return () => clearTimeout(timer)
  }, [source, delay])

  return value
}
